import React, { useEffect, useState } from "react";
import { FiMail, FiPhone, FiUser, FiEdit2, FiLock, FiCheck, FiX } from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoLocationSharp } from "react-icons/io5";
import { GrMoney } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { AuthApiService } from "../../../service/authApiService";
import { gstRegex } from "../../../helper/regex";
import "./style.css";

export default function UserProfile() {
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    address1: "",
    address2: "",
    gst: "",
  });

  const [editMode, setEditMode] = useState({
    name: false,
    phone: false,
    company: false,
    address: false,
    gst: false,
    password: false,
  });

  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    handleUserProfile();
  }, []);

  const handleUserProfile = () => {
    loader.showLoading(true);
    AuthApiService.getProfile()
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          setData({
            firstName: res.data.firstName || "",
            lastName: res.data.lastName || "",
            email: res.data.email || "",
            phone: res.data.phone || "",
            companyName: res.data.companyName || "",
            address1: res.data.address1 || "",
            address2: res.data.address2 || "",
            gst: res.data.gst || "",
          });
        } else {
          navigate('/login', { replace: true });
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const toggleEdit = (field) => {
    setEditMode({ ...editMode, [field]: !editMode[field] });
  };

  const validateGst = () => {
    if (data.gst && !data.gst.match(gstRegex)) {
      setErrors({ ...errors, gst: "Invalid GST number" });
      return false;
    }
    return true;
  };

  const updateProfile = () => {
    if (!validateGst()) return;
    
    loader.showLoading(true);
    AuthApiService.updateProfile(JSON.stringify(data))
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast("Profile updated successfully", ToastTypes.SUCCESS);
          setEditMode({ name: false, phone: false, company: false, address: false, gst: false, password: false });
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  const updatePassword = () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      message.showToast("Please fill all password fields", ToastTypes.WARNING);
      return;
    }
    if (passwords.new !== passwords.confirm) {
      message.showToast("New passwords don't match", ToastTypes.ERROR);
      return;
    }
    if (passwords.new.length < 6) {
      message.showToast("Password must be at least 6 characters", ToastTypes.ERROR);
      return;
    }

    loader.showLoading(true);
    AuthApiService.updatePassword({ currentPassword: passwords.current, newPassword: passwords.new })
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          message.showToast("Password updated successfully", ToastTypes.SUCCESS);
          setPasswords({ current: "", new: "", confirm: "" });
          setEditMode({ ...editMode, password: false });
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
        loader.showLoading(false);
      });
  };

  return (
    <div className="sp-page">
      {/* Profile Header */}
      <div className="sp-header">
        <div className="sp-avatar">
          <span>{data.firstName?.charAt(0) || "U"}{data.lastName?.charAt(0) || ""}</span>
        </div>
        <div className="sp-header-info">
          <h2 className="sp-header-name">{data.firstName} {data.lastName}</h2>
          <p className="sp-header-email">{data.email}</p>
        </div>
      </div>

      <div className="sp-content">
        {/* User Details Section */}
        <div className="sp-section">
          <div className="sp-section-header">
            <h3 className="sp-section-title">User Details</h3>
          </div>
          
          <div className="sp-fields">
            {/* Name */}
            <div className="sp-field">
              <div className="sp-field-icon"><FiUser /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">Full Name</label>
                {editMode.name ? (
                  <div className="sp-field-edit-row">
                    <input 
                      type="text" 
                      className="sp-input" 
                      value={data.firstName}
                      name="firstName"
                      onChange={handleChange}
                      placeholder="First Name"
                    />
                    <input 
                      type="text" 
                      className="sp-input" 
                      value={data.lastName}
                      name="lastName"
                      onChange={handleChange}
                      placeholder="Last Name"
                    />
                  </div>
                ) : (
                  <span className="sp-field-value">{data.firstName} {data.lastName}</span>
                )}
              </div>
              <button className="sp-edit-btn" onClick={() => toggleEdit('name')}>
                {editMode.name ? <FiX /> : <FiEdit2 />}
              </button>
            </div>

            {/* Phone */}
            <div className="sp-field">
              <div className="sp-field-icon"><FiPhone /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">Phone Number</label>
                {editMode.phone ? (
                  <input 
                    type="tel" 
                    className="sp-input" 
                    value={data.phone}
                    name="phone"
                    onChange={handleChange}
                    placeholder="Phone Number"
                  />
                ) : (
                  <span className="sp-field-value">{data.phone || "Not provided"}</span>
                )}
              </div>
              <button className="sp-edit-btn" onClick={() => toggleEdit('phone')}>
                {editMode.phone ? <FiX /> : <FiEdit2 />}
              </button>
            </div>

            {/* Email - Read Only */}
            <div className="sp-field">
              <div className="sp-field-icon"><FiMail /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">Email Address</label>
                <span className="sp-field-value">{data.email}</span>
              </div>
              <span className="sp-verified-badge">Verified</span>
            </div>
          </div>
        </div>

        {/* Company Details Section */}
        <div className="sp-section">
          <div className="sp-section-header">
            <h3 className="sp-section-title">Company Details</h3>
          </div>
          
          <div className="sp-fields">
            {/* Company Name */}
            <div className="sp-field">
              <div className="sp-field-icon"><HiOutlineBuildingOffice2 /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">Company Name</label>
                {editMode.company ? (
                  <input 
                    type="text" 
                    className="sp-input" 
                    value={data.companyName}
                    name="companyName"
                    onChange={handleChange}
                    placeholder="Company Name"
                  />
                ) : (
                  <span className="sp-field-value">{data.companyName || "Not provided"}</span>
                )}
              </div>
              <button className="sp-edit-btn" onClick={() => toggleEdit('company')}>
                {editMode.company ? <FiX /> : <FiEdit2 />}
              </button>
            </div>

            {/* Address */}
            <div className="sp-field">
              <div className="sp-field-icon"><IoLocationSharp /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">Company Address</label>
                {editMode.address ? (
                  <div className="sp-field-edit-col">
                    <input 
                      type="text" 
                      className="sp-input" 
                      value={data.address1}
                      name="address1"
                      onChange={handleChange}
                      placeholder="Address Line 1"
                    />
                    <input 
                      type="text" 
                      className="sp-input" 
                      value={data.address2}
                      name="address2"
                      onChange={handleChange}
                      placeholder="Address Line 2"
                    />
                  </div>
                ) : (
                  <span className="sp-field-value">
                    {data.address1 || data.address2 
                      ? `${data.address1}${data.address1 && data.address2 ? ', ' : ''}${data.address2}` 
                      : "Not provided"}
                  </span>
                )}
              </div>
              <button className="sp-edit-btn" onClick={() => toggleEdit('address')}>
                {editMode.address ? <FiX /> : <FiEdit2 />}
              </button>
            </div>

            {/* GST */}
            <div className="sp-field">
              <div className="sp-field-icon"><GrMoney /></div>
              <div className="sp-field-content">
                <label className="sp-field-label">GST Number</label>
                {editMode.gst ? (
                  <div className="sp-field-edit-col">
                    <input 
                      type="text" 
                      className="sp-input" 
                      value={data.gst}
                      name="gst"
                      onChange={handleChange}
                      placeholder="GST Number"
                      maxLength="15"
                    />
                    {errors.gst && <span className="sp-error">{errors.gst}</span>}
                  </div>
                ) : (
                  <span className="sp-field-value">{data.gst || "Not provided"}</span>
                )}
              </div>
              <button className="sp-edit-btn" onClick={() => toggleEdit('gst')}>
                {editMode.gst ? <FiX /> : <FiEdit2 />}
              </button>
            </div>
          </div>
        </div>

        {/* Password Section */}
        <div className="sp-section">
          <div className="sp-section-header">
            <h3 className="sp-section-title">Security</h3>
            <button 
              className="sp-change-password-btn" 
              onClick={() => toggleEdit('password')}
            >
              {editMode.password ? 'Cancel' : 'Change Password'}
            </button>
          </div>
          
          {editMode.password && (
            <div className="sp-password-fields">
              <div className="sp-password-field">
                <div className="sp-field-icon"><FiLock /></div>
                <div className="sp-field-content">
                  <label className="sp-field-label">Current Password</label>
                  <input 
                    type="password" 
                    className="sp-input" 
                    value={passwords.current}
                    onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>
              </div>
              <div className="sp-password-field">
                <div className="sp-field-icon"><FiLock /></div>
                <div className="sp-field-content">
                  <label className="sp-field-label">New Password</label>
                  <input 
                    type="password" 
                    className="sp-input" 
                    value={passwords.new}
                    onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>
              </div>
              <div className="sp-password-field">
                <div className="sp-field-icon"><FiLock /></div>
                <div className="sp-field-content">
                  <label className="sp-field-label">Confirm New Password</label>
                  <input 
                    type="password" 
                    className="sp-input" 
                    value={passwords.confirm}
                    onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
              <button className="sp-update-password-btn" onClick={updatePassword}>
                <FiCheck /> Update Password
              </button>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="sp-actions">
          <button className="sp-save-btn" onClick={updateProfile}>
            <FiCheck /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
